import { Claim } from '../types/claim';
import { Role } from '../types/role';
import { User } from '../types/user';
import _ from 'lodash';

export const RolesClaimsMapping: { [role in Role]: Claim[] } = {
    admin: [],

    adminActive: [
        //
        { name: 'impersonate' },
        { name: 'sync' },
        { name: 'read', forState: null, forUser: null },
        { name: 'write', forState: ['draft', 'clearing', 'approval', 'closing'], forUser: null },
        { name: 'delete', forState: null, forUser: null },
        {
            name: 'transition',
            fromState: ['draft', 'clearing', 'approval', 'closing', 'completed', 'canceled'],
            toState: null,
            forUser: null,
        },
        {
            name: 'transition',
            fromState: ['payment'],
            toState:
                globalThis.config?.PAYMENT_SERVICE === 'manual'
                    ? null
                    : ['draft', 'clearing', 'approval', 'closing', 'canceled'],
            forUser: null,
        },
        { name: 'report' },
        { name: 'editAdditionalApprovers', forUser: null },
        { name: 'editSubsitution', forUser: null },
        { name: 'acceptDuplicate', forUser: null },
        { name: 'reviewDuplicate', forUser: null },
        { name: 'payment.setup' },
        { name: 'payment.read' },
        { name: 'payment.request' },
    ],

    backOffice: [
        {
            name: 'read',
            forState: ['clearing', 'approval', 'closing', 'payment', 'completed', 'canceled'],
            forUser: null,
        },
        { name: 'write', forState: 'clearing', forUser: null },
        { name: 'delete', forState: 'clearing', forUser: null },

        // Transitions
        {
            name: 'transition',
            fromState: 'clearing',
            toState: ['approval', 'draft', 'canceled'],
            forUser: null,
        },
        {
            name: 'transition',
            fromState: 'approval',
            toState: ['clearing', 'draft', 'canceled'],
            forUser: null,
        },
        {
            name: 'transition',
            fromState: 'payment',
            toState: globalThis.config?.PAYMENT_SERVICE === 'manual' ? ['completed'] : [],
            forUser: null,
        },

        { name: 'report' },
        { name: 'reviewDuplicate', forUser: null },
        { name: 'payment.setup' },
        { name: 'payment.read' },
        { name: 'payment.request' },
    ],

    finance: [
        {
            name: 'read',
            forState: ['clearing', 'approval', 'closing', 'payment', 'completed', 'canceled'],
            forUser: null,
        },

        // Transitions from/to closing
        {
            name: 'transition',
            fromState: 'approval',
            toState: ['closing', 'clearing', 'draft', 'canceled'],
            forUser: null,
        },
        {
            name: 'transition',
            fromState: 'closing',
            toState: ['payment', 'approval', 'clearing', 'draft', 'canceled'],
            forUser: null,
        },
        {
            name: 'transition',
            fromState: ['payment', 'completed'],
            toState: ['closing', 'approval', 'clearing', 'draft', 'canceled'],
            forUser: null,
        },

        { name: 'report' },
        { name: 'reviewDuplicate', forUser: null },
        { name: 'payment.setup' },
        { name: 'payment.read' },
        { name: 'payment.request' },
    ],
};

export function hasRole(user?: User, ...roles: Role[]) {
    return _.intersection(user?.activeRoles?.map((x) => x.role) ?? [], roles).length > 0;
}

export function resolveClaims(user: User, recursive = true) {
    const claims = [...(user.claims ?? [])];

    claims.push(...(globalThis.config?.ALL_USERS_ADDITIONAL_CLAIMS ?? []));

    // Claims inherited from roles
    for (const { role } of user.activeRoles?.filter((x) => recursive || x.depth === 0) ?? []) {
        claims.push(...RolesClaimsMapping[role]);
    }

    // From the user's own roles only: activeRoles also holds roles inherited as a substitute.
    if (user.roles?.includes('admin')) {
        claims.push({ name: 'jobs.read' });
    }

    // Claims for own items
    claims.push({ name: 'read', forState: null, forUser: user.id });
    claims.push({ name: 'write', forUser: user.id, forState: 'draft' });
    claims.push({ name: 'delete', forUser: user.id, forState: 'draft' });
    claims.push({
        name: 'transition',
        forUser: user.id,
        fromState: 'draft',
        toState: ['clearing', 'canceled'],
    });
    claims.push({
        name: 'transition',
        forUser: user.id,
        fromState: ['clearing', 'approval', 'closing', 'payment', 'completed'],
        toState: 'draft',
    });
    claims.push({ name: 'editSubsitution', forUser: user.id });
    claims.push({ name: 'acceptDuplicate', forUser: user.id });

    // Claims for managed users' items
    const managedUserIds =
        user.managedUsers?.filter((u) => recursive || u.depth === 0).map((u) => u.id) ?? [];
    if (managedUserIds.length > 0) {
        claims.push({
            name: 'read',
            forUser: managedUserIds,
            forState: ['approval', 'closing', 'payment', 'completed', 'canceled'],
        });
        claims.push({
            name: 'transition',
            forUser: managedUserIds,
            fromState: 'approval',
            toState: ['closing', 'clearing', 'draft', 'canceled'],
        });
        claims.push({
            name: 'transition',
            forUser: managedUserIds,
            fromState: 'closing',
            toState: ['approval', 'clearing', 'draft', 'canceled'],
        });
        claims.push({ name: 'reviewDuplicate', forUser: managedUserIds });
    }

    return claims;
}

const test = (whitelist: unknown, demands: unknown) => {
    if (!whitelist) return true;
    if (!demands) return false;
    return _.castArray(demands).every((d) => _.castArray(whitelist).includes(d));
};

type Match<Name extends Claim['name']> = Omit<Claim & { name: Name }, 'name'>;
type MatchArgs<T> =
    Record<string, never> extends T
        ? [match?: T, resursive?: boolean]
        : [match: T, resursive?: boolean];

export const hasClaim = <Name extends Claim['name']>(
    userOrClaims: User | Claim[] | null | undefined,
    name: Name,
    ...[match, recursive = true]: MatchArgs<Match<Name>>
) => {
    const claims = Array.isArray(userOrClaims)
        ? userOrClaims
        : userOrClaims
          ? resolveClaims(userOrClaims, recursive)
          : [];

    return claims.some((claim) => {
        return (
            claim.name === name &&
            Object.entries(match ?? {}).every(([key, value]) => {
                return test(claim[key as keyof typeof claim], value);
            })
        );
    });
};

Object.assign(globalThis, { hasRole, resolveClaims, hasClaim });
